/*
 * toolboxedit.ts
 * Gegenereerd met Junie (AI), 25-5-2026 - Jelmer Jellema - Spin in het Web B.V.
 * Editscherm voor een toolbox-document.
 */

import {Component, OnInit} from '@angular/core';
import {DateTime} from 'luxon';
import {FormsModule} from '@angular/forms';
import {TabDirective, TabsetComponent} from 'ngx-bootstrap/tabs';
import {CommonModule} from '@angular/common';
import {EditorComponent, EditedComponent} from 'sihw-ngx';
import {SihwDatatable2Module} from 'sihw-ng/datatable2/datatable2.module';
import {NgSelectModule} from '@ng-select/ng-select';
import {SihwdatepickerModule} from 'sihw-ng/controls/sihwdatepicker/sihwdatepicker.module';
import {Editmodelscherm} from '../abstracts/editmodelscherm';
import {Toolbox} from '../../models/toolbox';
import {PersoneelvastToolbox} from '../../models/personeelvast_toolbox';
import {Functie} from '../../models/functie';
import {Personeel} from '../../models/personeel';
import {DatatableDef} from 'sihw-ng/datatable2/datatable2.component';
import {Basemodel} from '../../models/basemodel';
import {hash} from '../../app/types';
import type {Datarecord} from '../../services/api/api.service';
import {XlsDatatable} from 'sihw-ng/xlsx-datatable/xls-datatable';

@Component({
    standalone: true,
    selector: 'toolbox-edit',
    templateUrl: './toolboxedit.html',
    styleUrls: ['./toolboxedit.scss'],
    imports: [
        CommonModule,
        FormsModule,
        TabsetComponent,
        TabDirective,
        EditorComponent,
        EditedComponent,
        SihwDatatable2Module,
        SihwdatepickerModule,
        NgSelectModule,
        XlsDatatable
    ]
})
export class Toolboxedit extends Editmodelscherm<typeof Toolbox> implements OnInit {
    _starttitle = 'Toolbox';
    icon = 'fas fa-toolbox';

    protected model = Toolbox;
    protected defaultSubmodels = [PersoneelvastToolbox];

    lookupQueries = {
        functie: {
            model: Functie,
            suboptions: <[typeof Basemodel, hash<any>][]>[[Functie, {attributes: ['id', 'naam']}]],
            submodels: [],
            sortBy: 'naam',
            aclModel: Toolbox
        },
        personeel: {
            model: Personeel,
            suboptions: <[typeof Basemodel, hash<any>][]>[[Personeel, {attributes: ['id', 'voornaam','tussenvoegsel','achternaam','naam']}]],
            submodels: [],
            sortBy: 'naam',
            aclModel: Toolbox
        }
    };

    /** Definitie voor de datatable in de tab Geaccepteerd */
    readonly geaccepteerdTableDef: DatatableDef = {
        track: 'id',
        standaardsortering: {key: 'naam', richting: 1},
        velden: {
            naam: {
                label: 'Naam',
                type: 'string'
            },
            geaccepteerd: {
                label: 'Geaccepteerd',
                type: 'bool',
                exportformat: accept => accept ? 'Ja' : 'Nee'
            },
            createdAt: {
                label: 'Eerst getoond op',
                type: ['moment', {format: 'DD-MM-YYYY HH:mm'}]
            }
        }
    };

    /** Geaccepteerd-rijen verrijkt met personeelsnaam */
    geaccepteerdData: { id: number; naam: string; createdAt: string, geaccepteerd: boolean }[] = [];

    /** Alle beschikbare labels (opgehaald via Personeel.alleLabels) */
    beschikbareLabels: string[] = [];

    /** Geselecteerde labels als lokale binding voor ng-select */
    geselecteerdeLabels: string[] = [];

    /** Geselecteerde functie-ids als lokale binding voor ng-select */
    geselecteerdeFunctieIds: number[] = [];

    constructor() {
        super('warn');
    }

    override async ngOnInit(): Promise<void> {
        await super.ngOnInit();
        this.beschikbareLabels = await Personeel.alleLabels();
    }

    protected override async afterHaaldata(): Promise<void> {
        await super.afterHaaldata();
        this.geselecteerdeLabels = [...(this.record?.labels ?? [])];
        this.geselecteerdeFunctieIds = [...(this.record?.functie_ids ?? [])];
        this.bouwGeaccepteerdData();
    }

    protected override afterHaalLookups(): void {
        this.bouwGeaccepteerdData();
    }

    /**
     * Bouwt de geaccepteerdData-array op door personeelvast_toolbox-rijen te verrijken met de naam uit de personeel-lookup
     */
    private bouwGeaccepteerdData(): void {
        if (!this.record?.personeelvast_toolbox) {
            this.geaccepteerdData = [];
            return;
        }
        const personeelHash = this.lookupHashes['personeel'] as Record<number, Personeel> ?? {};
        this.geaccepteerdData = this.record.personeelvast_toolbox.map((pvt: PersoneelvastToolbox) => ({
            id: pvt.id,
            naam: (personeelHash[pvt.personeelvast_id] as Personeel)?.naam ?? `Personeelslid #${pvt.personeelvast_id}`,
            createdAt: pvt.createdAt,
            geaccepteerd: pvt.geaccepteerd
        }));
    }

    /**
     * Typecast voor de functies-lookup
     */
    get functieLookups(): Functie[] {
        return this.lookups['functie'] as Functie[] ?? [];
    }

    /**
     * Geeft de naam van een functie op basis van id
     */
    functieNaam(functieId: number): string {
        return (this.lookupHashes['functie']?.[functieId] as Functie)?.naam ?? `Functie #${functieId}`;
    }

    /**
     * Verwerkt een wijziging in de functieselectie vanuit ng-select
     */
    functiesGewijzigd(): void {
        this.record.functie_ids = [...(this.geselecteerdeFunctieIds ?? [])];
        this.setDirty();
    }

    /**
     * Verwerkt een wijziging in de labelselectie vanuit ng-select
     */
    labelsGewijzigd(): void {
        this.record.labels = [...(this.geselecteerdeLabels ?? [])];
        this.setDirty();
    }

    /**
     * De datum van vandaag als YYYY-MM-DD string, voor minDate/maxDate op de datepickers
     */
    get vandaag(): string {
        return DateTime.now().toISODate()!;
    }

    /**
     * De minimale begindatum: de originele begindatum als die al in het verleden ligt, anders vandaag
     */
    get minBegindatum(): string {
        return this.record?.orig('begindatum') || this.vandaag;
    }

    /**
     * De minimale einddatum: de originele einddatum als die al in het verleden ligt, anders de begindatum of vandaag
     */
    get minEinddatum(): string {
        return this.record?.orig('einddatum') || this.record?.begindatum || this.vandaag;
    }

    /**
     * Geeft een leesbare omschrijving van de doelgroep
     */
    get doelgroepOmschrijving(): string {
        const functies = this.record?.functie_ids ?? [];
        const labels = this.record?.labels ?? [];
        if (!functies.length && !labels.length) {
            return 'Al het personeel';
        }
        const delen: string[] = [];
        if (functies.length) {
            const namen = functies.map(id => this.functieNaam(id)).join(', ');
            delen.push(`functie ${namen}`);
        }
        if (labels.length) {
            delen.push(`label ${labels.join(', ')}`);
        }
        return `Personeel met ${delen.join(' of ')}`;
    }

    /**
     * Bereidt de savedata voor: verwijdert personeelvast_toolbox en zet de inhoud om van Quill Delta naar plain object
     */
    protected override savedata(): Datarecord {
        const data = super.savedata();
        delete data['personeelvast_toolbox'];
        if (data['inhoud']?.ops) {
            data['inhoud'] = {ops: data['inhoud'].ops};
        }
        return data;
    }

    /** UX-state voor toevoegen */
    ux = {
        nieuweFunctie: null as Functie | null,
        nieuwLabelKeuze: null as string | null,
        nieuwLabelInvoer: null as string | null
    };
}

Toolboxedit.register('Toolboxedit');
